import React, { useEffect, useState } from "react";
import { useSharedSelector } from "../../../store/SharedProvider";
import {
  selectSelectedRoom,
  selectSelectedRoomCleaningZones,
  selectSelectedRoomRedZones,
  selectSelectedRoomRuleZones,
} from "../../../store/room/room-selectors";
import { calculateAvailableSpace } from "../../../utils/map/zone-helper";
import ZonePlannerModeState from "../../../enums/robot/zone-planner/zone-planner-mode-state";
import { selectZonePlannerModeState } from "../../../store/room/zone-planner/zone-planner-selectors";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import DistanceUnitSet from "../../misc/DistanceUnitSet";
import { DistanceValueUnit } from "../../../enums/configuration/units";
import useDistanceUnitValue from "../../../hooks/useDistanceUnitValue";

const AreaInformation: React.FC = () => {
  const { t } = useTranslation();

  const cleaningZones = useSharedSelector((state) => selectSelectedRoomCleaningZones(state));
  const redZones = useSharedSelector((state) => selectSelectedRoomRedZones(state));
  const ruleZones = useSharedSelector((state) => selectSelectedRoomRuleZones(state));
  const roomData = useSharedSelector((state) => selectSelectedRoom(state));
  const zonePlannerMode = useSharedSelector((state) => selectZonePlannerModeState(state));

  const { multiplier, label } = useDistanceUnitValue(0, DistanceValueUnit.METERS_SQUARED);

  const [areaInformation, setAreaInformation] = useState({
    totalImageArea: 0,
    totalMappedArea: 0,
    availableMapArea: 0,
  });

  useEffect(() => {
    if (roomData?.map != null) {
      const getRoomAreaInformation = async () => {
        const availableMapArea = await calculateAvailableSpace(
          roomData.map!,
          cleaningZones
            .map((cz) => cz.points.map((p) => p.point))
            .concat(
              redZones
                .filter((rz) => zonePlannerMode === ZonePlannerModeState.EvacuationMode || !rz.evacuation)
                .map((rz) => rz.points.map((p) => p.point))
            )
            .concat(ruleZones.map((rz) => rz.points.map((p) => p.point))),
          roomData
        );

        const totalMappedArea = await calculateAvailableSpace(roomData.map!, [], roomData);

        const totalImageArea =
          roomData.mapInformation == null
            ? 0
            : roomData.mapInformation.height *
              roomData.mapInformation.resolution *
              roomData.mapInformation.width *
              roomData.mapInformation.resolution;

        setAreaInformation({ totalImageArea, totalMappedArea, availableMapArea });
      };
      getRoomAreaInformation();
    }
  }, [cleaningZones, redZones, ruleZones, roomData, zonePlannerMode]);

  if (zonePlannerMode === ZonePlannerModeState.EditMap) return null;

  return (
    <React.Fragment>
      <Typography variant="caption">
        {`${t("zoneplanner-total-map-area")}: ${(areaInformation.totalImageArea * multiplier).toFixed(2)}${label}`}
      </Typography>
      <Typography variant="caption">
        {`${t("zoneplanner-total-room-area")}: ${(areaInformation.totalMappedArea * multiplier).toFixed(2)}${label}`}
      </Typography>
      <Typography variant="caption">
        {`${t("zoneplanner-total-available-area")}: ${(areaInformation.availableMapArea * multiplier).toFixed(
          2
        )}${label}`}
      </Typography>
    </React.Fragment>
  );
};

export default AreaInformation;
